import { useEffect, useState } from 'react'
import { AlertTriangle, ArrowRight, CheckCircle2 } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { reportMarketDataStatus, resetMarketDataStatus, useMarketDataStatus } from '../../lib/marketDataStatusEvents'

export default function MarketDataOutageAlert() {
  const { sessionRef, user } = useAuth()
  const location = useLocation()
  const status = useMarketDataStatus()
  const [dismissed, setDismissed] = useState(null)
  const applyStatus = reportMarketDataStatus

  useEffect(() => () => resetMarketDataStatus(), [user?.id])

  useEffect(() => {
    const accessToken = sessionRef?.current?.access_token
    if (!accessToken) return
    let cancelled = false
    fetch('/api/market-data/operations-status', {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(response => response.ok ? response.json() : null)
      .then(result => { if (!cancelled && result) applyStatus(result) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [applyStatus, sessionRef, user?.id])

  // Chart failures and recoveries belong to their chart, not the app shell.
  if (status?.source === 'chart' || status?.state === 'chart-error') return null

  if (status?.recovered && dismissed !== status) {
    if (location.pathname === '/positions/workspace') return null
    const symbol = status.symbol
    const workspacePath = symbol
      ? `/positions/workspace?symbol=${encodeURIComponent(symbol)}`
      : '/positions/workspace'
    return (
      <div role="status" className="flex items-center justify-between gap-3 border-b border-forest/40 bg-forest/10 px-4 py-2 text-forest">
        <div className="flex min-w-0 items-center gap-2 text-xs font-semibold">
          <CheckCircle2 size={14} className="shrink-0" />
          <span>{status.symbol ? `${status.symbol} chart data loaded successfully.` : 'Chart data loaded successfully.'}</span>
        </div>
        <Link
          to={workspacePath}
          onClick={() => setDismissed(status)}
          className="flex shrink-0 items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-wide hover:underline"
        >
          {symbol ? `Open ${symbol} Chart` : 'Open Position Workspace'} <ArrowRight size={12} />
        </Link>
      </div>
    )
  }

  if (!status || status.operational) return null
  return (
    <div role="alert" className="flex items-center justify-between gap-3 border-b border-ember/40 bg-ember/10 px-4 py-2 text-ember">
      <div className="flex min-w-0 items-center gap-2 text-xs font-semibold">
        <AlertTriangle size={14} className="shrink-0" />
        <span>{status.message || 'Erevis market data is unavailable.'}</span>
      </div>
      {location.pathname !== '/settings' && <Link
        to="/settings?area=market-data"
        className="flex shrink-0 items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-wide hover:underline"
      >
        Review data status <ArrowRight size={12} />
      </Link>}
    </div>
  )
}
